import React, { useRef, useEffect, useState, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import "./DMapStyle.css";
import { bbox } from '@turf/turf';
import {
    Tipligheri,
    Maipith,
    Dwariknagar,
    PS0000149,
    PS000060,
    PS0000107,
    PS0000139,
    PS0000132,
    MER_CR_Demarcation2025,
    Paglam_CR_Demarcation2025,
    SHIVGURI_CR_Demarcation2025,
    Namsing_CR_Demarcation2025
} from "../../../../mock/dummyGeoJson";
import { FeatureCollection } from 'geojson';
import LayerControl from './LayerControl';
import { getMapStyleUrl, getMapAttribution, getMapStyleOptions } from '../../../../utils/mapStyles';
import { getDeviceLayersForProject, createDeviceLeafletIcon, DEVICE_ICONS_SVG } from '../../../../utils/mapLayerUtils';

interface DMapBioAnalysisProps {
    siteDropDownDetails: any;
}

const getBoundaryForSite = (siteName?: string): FeatureCollection | null => {
    if (siteName === 'Dwariknagar' || siteName === 'Drawiknagar') return Dwariknagar as FeatureCollection;
    if (siteName === 'Tipligheri') return Tipligheri as FeatureCollection;
    if (siteName === 'Maipith') return Maipith as FeatureCollection;
    if (siteName === 'PS0000149') return PS0000149 as FeatureCollection;
    if (siteName === 'PS000060') return PS000060 as FeatureCollection;
    if (siteName === 'PS0000107') return PS0000107 as FeatureCollection;
    if (siteName === 'PS0000139') return PS0000139 as FeatureCollection;
    if (siteName === 'PS0000132') return PS0000132 as FeatureCollection;
    if (siteName === 'Mer') return MER_CR_Demarcation2025 as FeatureCollection;
    if (siteName === 'Namsing Proxy' || siteName === 'Namsing Original') return Namsing_CR_Demarcation2025 as FeatureCollection;
    if (siteName === 'Paglam') return Paglam_CR_Demarcation2025 as FeatureCollection;
    if (siteName === 'Shivguri' || siteName === 'SHIVGURI_CR_Demarcation2025') return SHIVGURI_CR_Demarcation2025 as FeatureCollection;
    return null;
};

const DMapBioAnalysis: React.FC<DMapBioAnalysisProps> = ({ siteDropDownDetails }) => {
    const siteName = siteDropDownDetails?.site_name || 'Dwariknagar';
    const DEVICE_LAYERS = useMemo(() => getDeviceLayersForProject(siteName), [siteName]);

    const mapContainer = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<L.Map | null>(null);
    const [mapStyle, setMapStyle] = useState<string>('satellite');
    const [activeLayers, setActiveLayers] = useState<string[]>([]);
    const [layers, setLayers] = useState<{ id: string; name: string; opacity: number }[]>([]);
    const mapInitialized = useRef<boolean>(false);
    const tileLayerRef = useRef<L.TileLayer | null>(null);
    const initializeMap = (data: FeatureCollection) => {
        if (!mapContainer.current) return;
        if (mapRef.current) { 
            mapRef.current.remove(); 
            mapRef.current = null; 
        }

        const centerLatLng: [number, number] = [21.7125, 88.2625];
        mapRef.current = L.map(mapContainer.current as HTMLDivElement, { center: centerLatLng, zoom: 13, zoomControl: true });
        
        tileLayerRef.current = L.tileLayer(getMapStyleUrl(mapStyle), getMapStyleOptions(mapStyle)).addTo(mapRef.current);

        const boundaryLayer = L.geoJSON(data as any, {
            style: () => ({ fillColor: '#90EE90', fillOpacity: 0.3, color: '#006400', weight: 2 }),
            filter: (feature: any) => feature && feature.geometry && feature.geometry.type === 'Polygon',
            onEachFeature: (feature: any, layer: any) => {
                if (feature.properties) {
                    layer.bindPopup(Object.entries(feature.properties).map(([k, v]) => `<strong>${k}:</strong> ${v}`).join('<br/>'));
                }
            }
        }).addTo(mapRef.current);

        const deviceLayerGroups: Record<string, L.LayerGroup> = {};
        DEVICE_LAYERS.forEach(({ type }: any) => { 
            deviceLayerGroups[`device-${type.replace(/\s+/g, '-').toLowerCase()}`] = L.layerGroup(); 
        });

        try {
            const features = (data as any).features || [];
            features.forEach((f: any) => {
                if (!f || !f.geometry) return;
                if (f.geometry.type === 'Point') {
                    const coords = f.geometry.coordinates;
                    const latlng: [number, number] = [coords[1], coords[0]];
                    const type = f.properties?.Type || f.properties?.type || '';
                    const layerKey = `device-${String(type).replace(/\s+/g, '-').toLowerCase()}`;
                    const color = DEVICE_LAYERS.find((dl: any) => dl.type === type)?.color || '#ff5722';
                    const marker = L.marker(latlng, { icon: createDeviceLeafletIcon(type, color) });
                    if (f.properties) marker.bindPopup(Object.entries(f.properties).map(([k, v]) => `<strong>${k}:</strong> ${v}`).join('<br/>'));
                    deviceLayerGroups[layerKey]?.addLayer(marker);
                }
            });
        } catch (e) { console.warn('Failed to populate device layers', e); }

        (mapRef.current as any)._deviceLayerGroups = deviceLayerGroups;
        (mapRef.current as any)._boundaryLayer = boundaryLayer;

        Object.values(deviceLayerGroups).forEach(g => g.addTo(mapRef.current as L.Map));

        const polygons = data.features.filter((f: any) => f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon');
        setLayers([
            { id: 'boundary-fill', name: polygons[0]?.properties?.Name || 'Site Boundary', opacity: 0.3 },
            ...DEVICE_LAYERS.map((d: any) => ({ id: `device-${d.type.replace(/\s+/g, '-').toLowerCase()}`, name: d.label, opacity: 1 })),
        ]);
        setActiveLayers(['boundary-fill', ...DEVICE_LAYERS.map((d: any) => `device-${d.type.replace(/\s+/g, '-').toLowerCase()}`)]);

        try {
            const siteBbox = bbox(data as unknown as FeatureCollection);
            const bounds = L.latLngBounds([[siteBbox[1], siteBbox[0]], [siteBbox[3], siteBbox[2]]]);
            mapRef.current?.fitBounds(bounds, { padding: [40, 40] });
        } catch (err) {
            console.error("[DMapBioAnalysis] fitBounds error:", err);
        }
    };

    useEffect(() => {
        const boundary = getBoundaryForSite(siteName);
        if (boundary) {
            initializeMap(boundary);
            mapInitialized.current = true;
        }

        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
                mapInitialized.current = false;
            }
        };
    }, [siteName]);

    // Handle map style changes without recreating the map
    useEffect(() => {
        if (!mapRef.current || !mapInitialized.current || !tileLayerRef.current) return;
        mapRef.current.removeLayer(tileLayerRef.current);
        tileLayerRef.current = L.tileLayer(getMapStyleUrl(mapStyle), getMapStyleOptions(mapStyle)).addTo(mapRef.current);
    }, [mapStyle]);

    const toggleLayerVisibility = (layer: string) => {
        const isActive = activeLayers.includes(layer);
        setActiveLayers(isActive ? activeLayers.filter(id => id !== layer) : [...activeLayers, layer]);
        const map = mapRef.current as any;
        if (!map) return;
        const deviceGroups: Record<string, L.LayerGroup> = map._deviceLayerGroups || {};
        if (deviceGroups[layer]) {
            if (isActive) map.removeLayer(deviceGroups[layer]); else deviceGroups[layer].addTo(map);
            return;
        }
        const boundaryLayer: L.Layer = map._boundaryLayer;
        if (layer === 'boundary-fill' && boundaryLayer) {
            if (isActive) map.removeLayer(boundaryLayer); else (boundaryLayer as any).addTo(map);
        }
    };

    const handleOpacityChange = (layerId: string, newOpacity: number) => {
        setLayers(prev => prev.map(l => l.id === layerId ? { ...l, opacity: newOpacity } : l));
        const map = mapRef.current as any;
        if (!map) return;
        if (layerId === 'boundary-fill') {
            const b = map._boundaryLayer as L.GeoJSON | undefined;
            if (b) b.setStyle({ fillOpacity: newOpacity });
            return;
        }
        const deviceGroups: Record<string, L.LayerGroup> = map._deviceLayerGroups || {};
        const group = deviceGroups[layerId];
        if (group) group.eachLayer((lyr: any) => { if (lyr && typeof lyr.setStyle === 'function') try { lyr.setStyle({ fillOpacity: newOpacity, opacity: newOpacity }); } catch(e){} });
    };

    return (
        <div style={{ position: 'relative', height: '65vh', width: '99%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '8px 15px', backgroundColor: 'white', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, fontSize: '13px' }}>Devices:</span>
                {DEVICE_LAYERS.map(({ type, color, label }: any) => (
                    <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                        <div
                            style={{ display: 'flex', alignItems: 'center', color }}
                            dangerouslySetInnerHTML={{ __html: (DEVICE_ICONS_SVG[type] || '').replace('fill="#ffffff"', `fill="${color}"`) }}
                        />
                        <span style={{ fontWeight: 500 }}>{label}</span>
                    </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                    <div style={{ width: '14px', height: '14px', backgroundColor: 'rgba(144,238,144,0.6)', border: '1.5px solid #006400', borderRadius: '2px' }} />
                    <span>Site Boundary</span>
                </div>
            </div>

            <div ref={mapContainer} style={{ position: 'absolute', top: 40, bottom: 0, width: '100%' }} />

            <LayerControl
                layers={layers}
                activeLayers={activeLayers}
                toggleLayerVisibility={toggleLayerVisibility}
                handleOpacityChange={handleOpacityChange}
                mapStyle={mapStyle}
                setMapStyle={setMapStyle}
            />
        </div>
    );
};

export default DMapBioAnalysis;
